import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  updateCustomSchemas,
  updateCustomSchemasUserPortal,
} from '../../../../api/users-api';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { CustomSchema, CustomSchemaEntity, User } from '../../../../../types';

import { handleErrors } from '../../../../utils/errors';
import { useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
  UserViewRoles,
} from '../../../../utils/roles/client-roles';
import { userPortalPagesConfig } from '../../../../shared/user-portal-pages-config';
import { useLocation } from 'react-router-dom';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import SharedButton from '../../../../shared/buttons/shared-button';
import SharedTabHeader from '../../../../shared/shared-tab-header';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddCircle from '@mui/icons-material/AddCircle';
import Delete from '@mui/icons-material/Delete';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

interface CustomSchemasFormProps {
  user: User;
  fullSchemaData: CustomSchemaEntity[];
  directoryRoles: string[];
  changeLogRoles: string[];
  userViewRoles?: string[];
}

export default function CustomSchemasForm({
  user,
  fullSchemaData,
  directoryRoles,
  changeLogRoles,
  userViewRoles,
}: CustomSchemasFormProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const location = useLocation();

  const isInAdminView = !userViewRoles;

  const isUserPortal = location.pathname.includes(
    userPortalPagesConfig.userPortal.link
  );

  const shouldDisableField = isInAdminView
    ? !isAbleToSeeUIElement(
        directoryRoles,
        DirectoryRoles.EditCustomAttributes.value
      )
    : !isAbleToSeeUIElement(
        userViewRoles,
        UserViewRoles.EditCustomAttributes.value
      );

  const { mutate, isPending: updatingSchema } = useMutation({
    mutationFn: (data: { customSchemas: CustomSchema[] }) =>
      updateCustomSchemas({
        primaryEmail: user.primaryEmail,
        customSchemas: data.customSchemas,
      }),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'User custom attributes updated successfully!',
      });

      // best option right now, getting data structure right is...complicated
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.User, user.primaryEmail],
        });
      }, 1500);

      reset(getValues(), { keepValues: true });

      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error: error,
        snackbarMessage:
          'There was an issue updating the users custom attributes. Please try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.custom_attributes
          .update_custom_attributes,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.customAttributes,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: updatingSchemaUserPortal } =
    useMutation({
      mutationFn: (data: { customSchemas: CustomSchema[] }) =>
        updateCustomSchemasUserPortal({
          primaryEmail: user.primaryEmail,
          customSchemas: data.customSchemas,
        }),
      onSuccess: () => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'User custom attributes updated successfully!',
        });

        // best option right now, getting data structure right is...complicated
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: [UserKeys.User, user.primaryEmail],
          });
        }, 1500);

        reset(getValues(), { keepValues: true });
      },
      onError: (error) => {
        handleErrors({
          error: error,
          snackbarMessage:
            'There was an issue updating the users custom attributes. Please try again.',
        });
      },
      onSettled: () => {
        createEvent(
          analytics.userView.custom_attributes.update_custom_attributes,
          {
            module: ModuleList.modules.userView.name,
            section: ModuleList.modules.userView.sections.customAttributes,
          }
        );
      },
    });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { isDirty, dirtyFields, errors },
  } = useForm<{
    customSchemas: CustomSchema[];
  }>({
    defaultValues: {
      customSchemas:
        fullSchemaData?.map((schema) => {
          const userSchema = user.customSchemas?.find(
            (s) => s.schemaName === schema.schemaName
          );

          return {
            schemaName: schema.schemaName,
            values: schema.fields.map((field) => {
              const userValues = userSchema?.values.find(
                (value) => value.fieldDisplayName === field.fieldName
              );

              if (userValues) {
                return {
                  type: field.multiValued ? 'multi' : 'single',
                  fieldDisplayName: field.displayName,
                  values: userValues.values,
                };
              } else {
                return {
                  type: field.multiValued ? 'multi' : 'single',
                  fieldDisplayName: field.displayName,
                  values: [''],
                };
              }
            }),
          };
        }) || [],
    },
  });

  // https://github.com/orgs/react-hook-form/discussions/1991
  const getItemsDirtyData = <
    TData extends Record<keyof TDirtyItems, unknown>,
    TDirtyItems extends Record<string, unknown>,
  >(
    data: TData,
    dirtyItems: TDirtyItems
  ): Partial<TData> => {
    const dirtyItemsEntries = Object.entries(dirtyItems);

    return dirtyItemsEntries.reduce((dirtyData, [name, value]) => {
      if (typeof value !== 'object') {
        return { ...dirtyData, [name]: data[name] };
      }

      return {
        ...dirtyData,
        [name]: getItemsDirtyData(
          data[name] as TData,
          dirtyItems[name] as TDirtyItems
        ),
      };
    }, {});
  };

  function mutateData() {
    const updatedData: CustomSchema[] = [];

    const formData = getValues();

    const dirtyData = getItemsDirtyData(formData, dirtyFields);

    formData.customSchemas.forEach((schema, schemaIndex) => {
      if (dirtyData.customSchemas === undefined) return;
      if (dirtyData.customSchemas[schemaIndex] === undefined) return;

      // need to get fieldName, not display name off fullSchemaData
      const schemaFields = fullSchemaData?.find(
        (s) => s.schemaName === schema.schemaName
      )?.fields;

      if (!schemaFields) return;

      updatedData.push({
        schemaName: schema.schemaName,
        values: schema.values
          .filter(
            (_f, i) =>
              dirtyData.customSchemas &&
              dirtyData.customSchemas[schemaIndex].values[i] !== undefined
          )
          .map((field) => {
            const schemaIndex = schema.values.findIndex(
              (s) => s.fieldDisplayName === field.fieldDisplayName
            );

            const fieldName = schemaFields.find(
              (f) =>
                f.displayName === schema.values[schemaIndex].fieldDisplayName
            )?.fieldName;

            return {
              ...field,
              fieldDisplayName: fieldName || field.fieldDisplayName,
              values: field.values,
            };
          }),
      });
    });
    return updatedData;
  }

  const onSubmit: SubmitHandler<{ customSchemas: CustomSchema[] }> = () => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.EditCustomAttributes.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const updatedData = mutateData();

      if (!isInAdminView) {
        userPortalMutate({ customSchemas: updatedData });
      } else {
        mutate({ customSchemas: updatedData });
      }
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      sx={{
        position: 'relative',
        height: '100%',
        ...(isUserPortal && { p: 2, pt: 1 }),
      }}
    >
      {isUserPortal && <SharedTabHeader title="Custom Attributes" noDivider />}
      <Stack
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
        sx={
          !isUserPortal
            ? { position: 'absolute', top: -60, right: 0 }
            : { position: 'absolute', top: 12, right: 20 }
        }
      >
        <SharedIconButton
          variant="refresh"
          tooltipLabel="Refresh"
          onClick={() => {
            queryClient.invalidateQueries({
              queryKey: [UserKeys.CustomSchemas],
            });
            queryClient.invalidateQueries({
              queryKey: [UserKeys.User],
            });
          }}
        />
        <SharedButton
          variant="primary"
          disabled={
            !isDirty ||
            updatingSchema ||
            updatingSchemaUserPortal ||
            shouldDisableField
          }
          loading={updatingSchema || updatingSchemaUserPortal}
          type="submit"
        >
          Save
        </SharedButton>
      </Stack>
      <Box sx={{ flex: 1, height: '100%', overflowY: 'auto' }}>
        <Stack
          sx={{ p: 2, justifyContent: 'center', maxWidth: 1000, mx: 'auto' }}
        >
          {fullSchemaData.map((schema, schemaIndex) => {
            return (
              <Accordion key={schema.schemaId} defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls={`${schema.displayName}-content`}
                  id={`${schema.displayName}-header`}
                >
                  <Typography>{schema.displayName}</Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    maxHeight: 400,
                    overflowY: 'auto',
                  }}
                >
                  {schema.fields.map((fieldSpec, fieldIndex) => {
                    if (fieldSpec.fieldType === 'BOOL') {
                      return (
                        <Controller
                          name={`customSchemas.${schemaIndex}.values.${fieldIndex}.values.0`}
                          control={control}
                          key={fieldSpec.fieldId}
                          render={({ field: props }) => {
                            return (
                              <FormControl
                                sx={{ mx: 2, my: 1 }}
                                disabled={shouldDisableField}
                              >
                                <FormLabel>{fieldSpec.displayName}</FormLabel>
                                <RadioGroup
                                  aria-labelledby="radio-buttons-group-label"
                                  defaultValue=""
                                  name="radio-buttons-group"
                                  value={props.value}
                                  onChange={(e) =>
                                    props.onChange(e.target.value)
                                  }
                                  row
                                >
                                  <FormControlLabel
                                    value="true"
                                    control={<Radio />}
                                    label="Yes"
                                  />
                                  <FormControlLabel
                                    value="false"
                                    control={<Radio />}
                                    label="No"
                                  />
                                  <FormControlLabel
                                    value=""
                                    control={<Radio />}
                                    label="Not Applicable"
                                  />
                                </RadioGroup>
                              </FormControl>
                            );
                          }}
                        />
                      );
                    } else if (
                      fieldSpec.fieldType === 'STRING' ||
                      fieldSpec.fieldType === 'PHONE' ||
                      fieldSpec.fieldType === 'EMAIL'
                    ) {
                      if (fieldSpec.multiValued) {
                        const values = watch(
                          `customSchemas.${schemaIndex}.values.${fieldIndex}.values`
                        );

                        const asdf =
                          values?.map((_value, valueIndex) => {
                            return (
                              <Stack
                                flexDirection="row"
                                // eslint-disable-next-line react-x/no-array-index-key
                                key={`${fieldSpec.fieldId}-${valueIndex}`}
                                alignItems="baseline"
                                gap={1}
                              >
                                <Controller
                                  name={`customSchemas.${schemaIndex}.values.${fieldIndex}.values.${valueIndex}`}
                                  control={control}
                                  rules={
                                    fieldSpec.fieldType === 'EMAIL'
                                      ? {
                                          pattern: {
                                            value:
                                              /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: 'Invalid email address.',
                                          },
                                        }
                                      : {}
                                  }
                                  render={({ field }) => {
                                    return (
                                      <TextField
                                        {...field}
                                        label={fieldSpec.displayName}
                                        variant="outlined"
                                        size="small"
                                        autoComplete="off"
                                        onChange={(e) => {
                                          field.onChange(e.target.value);
                                        }}
                                        disabled={shouldDisableField}
                                        error={
                                          !!errors?.customSchemas?.[schemaIndex]
                                            ?.values?.[fieldIndex]?.values?.[
                                            valueIndex
                                          ]
                                        }
                                        helperText={
                                          errors?.customSchemas?.[schemaIndex]
                                            ?.values?.[fieldIndex]?.values?.[
                                            valueIndex
                                          ]?.message || ' '
                                        }
                                      />
                                    );
                                  }}
                                />
                                <Tooltip title="Remove value" arrow>
                                  <span>
                                    <IconButton
                                      onClick={() => {
                                        const newValues =
                                          values?.filter(
                                            (_v, i) => i !== valueIndex
                                          ) || '';

                                        setValue(
                                          `customSchemas.${schemaIndex}.values.${fieldIndex}.values`,
                                          newValues,
                                          {
                                            shouldDirty: true,
                                          }
                                        );
                                      }}
                                      disabled={shouldDisableField}
                                      sx={{ position: 'relative', top: 6 }}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            );
                          }) || [];

                        return (
                          <Stack
                            flexDirection="row"
                            gap={1}
                            flexWrap="wrap"
                            alignItems="baseline"
                            key={`${fieldSpec.fieldId}`}
                          >
                            {asdf}
                            <Tooltip title="Add another value" arrow>
                              <span>
                                <IconButton
                                  onClick={() => {
                                    setValue(
                                      `customSchemas.${schemaIndex}.values.${fieldIndex}.values`,
                                      [...values, ''],
                                      {
                                        shouldDirty: true,
                                      }
                                    );
                                  }}
                                  disabled={shouldDisableField}
                                  sx={{ position: 'relative', top: 6 }}
                                >
                                  <AddCircle />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        );
                      } else {
                        return (
                          <Controller
                            name={`customSchemas.${schemaIndex}.values.${fieldIndex}.values.0`}
                            control={control}
                            key={fieldSpec.fieldId}
                            rules={
                              fieldSpec.fieldType === 'EMAIL'
                                ? {
                                    pattern: {
                                      value:
                                        /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                      message: 'Invalid email address.',
                                    },
                                  }
                                : {}
                            }
                            render={({ field }) => {
                              return (
                                <TextField
                                  {...field}
                                  label={fieldSpec.displayName}
                                  variant="outlined"
                                  size="small"
                                  autoComplete="off"
                                  onChange={(e) => {
                                    field.onChange(e.target.value);
                                  }}
                                  sx={{ width: 400 }}
                                  disabled={shouldDisableField}
                                  error={
                                    !!errors?.customSchemas?.[schemaIndex]
                                      ?.values?.[fieldIndex]?.values?.[0]
                                  }
                                  helperText={
                                    errors?.customSchemas?.[schemaIndex]
                                      ?.values?.[fieldIndex]?.values?.[0]
                                      ?.message || ' '
                                  }
                                />
                              );
                            }}
                          />
                        );
                      }
                    } else if (
                      fieldSpec.fieldType === 'INT64' ||
                      fieldSpec.fieldType === 'DOUBLE'
                    ) {
                      return (
                        <Controller
                          name={`customSchemas.${schemaIndex}.values.${fieldIndex}.values.0`}
                          control={control}
                          key={fieldSpec.fieldId}
                          render={({ field }) => {
                            return (
                              <TextField
                                {...field}
                                label={fieldSpec.displayName}
                                variant="outlined"
                                size="small"
                                type="number"
                                disabled={shouldDisableField}
                                placeholder={
                                  fieldSpec.fieldType === 'DOUBLE'
                                    ? '0.00'
                                    : '0'
                                }
                                inputProps={{
                                  inputMode: 'numeric',
                                  pattern: '[0-9]*',
                                  step:
                                    fieldSpec.fieldType === 'DOUBLE'
                                      ? '0.01'
                                      : '1',
                                }}
                                sx={{ width: 400 }}
                                helperText={' '}
                              />
                            );
                          }}
                        />
                      );
                    } else if (fieldSpec.fieldType === 'DATE') {
                      return (
                        <Controller
                          name={`customSchemas.${schemaIndex}.values.${fieldIndex}.values.0`}
                          control={control}
                          key={fieldSpec.fieldId}
                          render={({ field }) => {
                            return (
                              <DatePicker
                                {...field}
                                label={fieldSpec.displayName}
                                value={
                                  field.value === '' ? null : dayjs(field.value)
                                }
                                onChange={(value) => {
                                  field.onChange(value?.format('YYYY-MM-DD'));
                                }}
                                sx={{ width: 400 }}
                                disabled={shouldDisableField}
                                slotProps={{
                                  textField: {
                                    helperText: 'MM/DD/YYYY',
                                  },
                                }}
                              />
                            );
                          }}
                        />
                      );
                    } else {
                      return (
                        <Controller
                          name={`customSchemas.${schemaIndex}.values.${fieldIndex}.values.0`}
                          control={control}
                          key={fieldSpec.fieldId}
                          render={({ field }) => {
                            return (
                              <TextField
                                {...field}
                                label={fieldSpec.displayName}
                                variant="outlined"
                                size="small"
                                autoComplete="off"
                                onChange={(e) => {
                                  field.onChange(e.target.value);
                                }}
                                disabled={shouldDisableField}
                                sx={{ width: 400 }}
                                helperText={' '}
                              />
                            );
                          }}
                        />
                      );
                    }
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      </Box>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const updatedData = mutateData();
          mutate({ customSchemas: updatedData });
        }}
        mutateLoading={updatingSchema}
      />
    </Box>
  );
}
